import { useState, useRef, useEffect } from 'react';
import { Client } from '../../lib/supabase';

interface ExportCreatorsButtonProps {
  clients: Client[];
  filteredClients: Client[];
}

type ExportFormat = 'csv' | 'xlsx' | 'pdf';

export default function ExportCreatorsButton({ clients, filteredClients }: ExportCreatorsButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportScope, setExportScope] = useState<'all' | 'filtered'>('filtered');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getExportData = (): Client[] => {
    return exportScope === 'filtered' ? filteredClients : clients;
  };

  const formatCurrency = (val: number): string =>
    `R$ ${val.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  const getStatusLabel = (status: string): string =>
    status === 'active' ? 'Ativo' : 'Inativo';

  const exportToCSV = () => {
    const data = getExportData();
    if (data.length === 0) return;

    const headers = [
      'Nome', 'Email', 'Telefone', 'CPF/CNPJ', 'Plataforma', 'Categoria', 'Status',
      'Seguidores', 'GMV Geral', 'GMV 7d', 'GMV 14d', 'GMV 28d', 'GMV 30d',
      'Comissão Orgânica %', 'Comissão Tráfego %', 'Produtos Divulgados',
      'Vídeos 7d', 'Vídeos 14d', 'Vídeos 28d', 'Vídeos 30d', 'Lives 30d',
      'Links TikTok', 'CEP', 'Endereço', 'Número', 'Complemento', 'Bairro',
      'Cidade', 'Estado', 'Chave PIX', 'Tipo PIX', 'Amostra Enviada',
      'Data Envio Amostra', 'Observação Amostra', 'Código Rastreio',
      'Link Grupo WhatsApp', 'Notas', 'Criado em', 'Atualizado em'
    ];

    const rows = data.map(client => [
      client.name,
      client.email || '',
      client.phone || '',
      client.cpf_cnpj || '',
      client.platform || '',
      client.category || '',
      getStatusLabel(client.status),
      client.followers || 0,
      client.gmv_geral || 0,
      client.gmv_interno_7d || 0,
      client.gmv_interno_14d || 0,
      client.gmv_interno_28d || 0,
      client.gmv_interno_30d || 0,
      client.comissao_organica || 0,
      client.comissao_trafego || 0,
      client.produtos_divulgados || '',
      client.videos_7d || 0,
      client.videos_14d || 0,
      client.videos_28d || 0,
      client.videos_30d || 0,
      client.lives_30d || 0,
      (client.tiktok_links || []).join('; '),
      client.endereco_cep || '',
      client.endereco_rua || '',
      client.endereco_numero || '',
      client.endereco_complemento || '',
      client.endereco_bairro || '',
      client.endereco_cidade || '',
      client.endereco_estado || '',
      client.chave_pix || '',
      client.chave_pix_tipo || '',
      client.amostra_enviada ? 'Sim' : 'Não',
      formatDate(client.amostra_data_envio),
      client.amostra_observacao || '',
      client.codigo_rastreio || '',
      client.whatsapp_group_link || '',
      client.notes || '',
      formatDate(client.created_at),
      formatDate(client.updated_at)
    ]);

    const escapeCSV = (value: string | number): string => {
      const str = String(value);
      if (str.includes(',') || str.includes(';') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(escapeCSV).join(';'))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `creators_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToXLSX = async () => {
    const data = getExportData();
    if (data.length === 0) return;

    try {
      const XLSX = await import('xlsx');

      const exportData = data.map(client => ({
        'Nome': client.name,
        'Email': client.email || '',
        'Telefone': client.phone || '',
        'CPF/CNPJ': client.cpf_cnpj || '',
        'Plataforma': client.platform || '',
        'Categoria': client.category || '',
        'Status': getStatusLabel(client.status),
        'Seguidores': client.followers || 0,
        'GMV Geral': client.gmv_geral || 0,
        'GMV 7d': client.gmv_interno_7d || 0,
        'GMV 14d': client.gmv_interno_14d || 0,
        'GMV 28d': client.gmv_interno_28d || 0,
        'GMV 30d': client.gmv_interno_30d || 0,
        'Comissão Orgânica %': client.comissao_organica || 0,
        'Comissão Tráfego %': client.comissao_trafego || 0,
        'Produtos Divulgados': client.produtos_divulgados || '',
        'Vídeos 7d': client.videos_7d || 0,
        'Vídeos 14d': client.videos_14d || 0,
        'Vídeos 28d': client.videos_28d || 0,
        'Vídeos 30d': client.videos_30d || 0,
        'Lives 30d': client.lives_30d || 0,
        'Links TikTok': (client.tiktok_links || []).join(', '),
        'CEP': client.endereco_cep || '',
        'Endereço': client.endereco_rua || '',
        'Número': client.endereco_numero || '',
        'Complemento': client.endereco_complemento || '',
        'Bairro': client.endereco_bairro || '',
        'Cidade': client.endereco_cidade || '',
        'Estado': client.endereco_estado || '',
        'Chave PIX': client.chave_pix || '',
        'Tipo PIX': client.chave_pix_tipo || '',
        'Amostra Enviada': client.amostra_enviada ? 'Sim' : 'Não',
        'Data Envio Amostra': formatDate(client.amostra_data_envio),
        'Observação Amostra': client.amostra_observacao || '',
        'Código Rastreio': client.codigo_rastreio || '',
        'Link Grupo WhatsApp': client.whatsapp_group_link || '',
        'Notas': client.notes || '',
        'Criado em': formatDate(client.created_at),
        'Atualizado em': formatDate(client.updated_at)
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Creators');

      const colWidths: { [key: string]: number } = {};
      exportData.forEach(row => {
        Object.keys(row).forEach(key => {
          const value = String(row[key as keyof typeof row]);
          colWidths[key] = Math.max(colWidths[key] || 0, value.length, key.length);
        });
      });
      ws['!cols'] = Object.keys(colWidths).map(key => ({ wch: Math.min(colWidths[key] + 2, 50) }));

      XLSX.writeFile(wb, `creators_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Erro ao exportar XLSX:', error);
    }
  };

  const exportToPDF = async () => {
    const data = getExportData();
    if (data.length === 0) return;

    try {
      const { jsPDF } = await import('jspdf');
      const { autoTable } = await import('jspdf-autotable');

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();

      doc.setFontSize(16);
      doc.setTextColor(0, 74, 173);
      doc.text('Relatório de Creators', 14, 20);

      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')} - Total: ${data.length} creators`, 14, 28);

      const headers = [
        'Nome', 'Email', 'Telefone', 'Status', 'Seguidores', 'GMV Geral',
        'Com. Org%', 'Com. Trf%', 'Vídeos 30d', 'Lives 30d', 'TikTok'
      ];

      const tableData = data.map(client => [
        client.name,
        client.email || '-',
        client.phone || '-',
        getStatusLabel(client.status),
        (client.followers || 0).toLocaleString('pt-BR'),
        formatCurrency(client.gmv_geral || 0),
        `${client.comissao_organica || 0}%`,
        `${client.comissao_trafego || 0}%`,
        String(client.videos_30d || 0),
        String(client.lives_30d || 0),
        (client.tiktok_links || []).length > 0 ? `${client.tiktok_links.length} link(s)` : '-'
      ]);

      autoTable(doc, {
        head: [headers],
        body: tableData,
        startY: 35,
        styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
        headStyles: { fillColor: [0, 74, 173], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        columnStyles: {
          0: { cellWidth: 35 },
          1: { cellWidth: 40 },
          2: { cellWidth: 25 },
          3: { cellWidth: 15 },
          4: { cellWidth: 18, halign: 'right' },
          5: { cellWidth: 22, halign: 'right' },
          6: { cellWidth: 12, halign: 'center' },
          7: { cellWidth: 12, halign: 'center' },
          8: { cellWidth: 15, halign: 'center' },
          9: { cellWidth: 15, halign: 'center' },
          10: { cellWidth: 20 }
        },
        margin: { left: 10, right: 10 }
      });

      const pageCount = doc.internal.pages.length - 1;
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(
          `Página ${i} de ${pageCount}`,
          pageWidth - 30,
          doc.internal.pageSize.getHeight() - 10
        );
      }

      doc.save(`creators_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
    }
  };

  const handleExport = async (format: ExportFormat) => {
    setIsExporting(true);
    setIsOpen(false);
    try {
      if (format === 'csv') exportToCSV();
      else if (format === 'xlsx') await exportToXLSX();
      else if (format === 'pdf') await exportToPDF();
    } finally {
      setIsExporting(false);
    }
  };

  const hasData = (exportScope === 'filtered' ? filteredClients : clients).length > 0;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isExporting}
        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isExporting ? (
          <>
            <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
            <span>Exportando...</span>
          </>
        ) : (
          <>
            <i className="ri-download-line text-sm"></i>
            <span>Exportar</span>
            <i className={`ri-arrow-down-s-line text-xs transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
          </>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl border border-gray-100 z-50 animate-[fadeIn_0.15s_ease-out] overflow-hidden">
          <div className="p-3 border-b border-gray-100">
            <p className="text-xs font-medium text-gray-500 mb-2">Quais creators exportar?</p>
            <div className="flex gap-2">
              <button
                onClick={() => setExportScope('filtered')}
                className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-all cursor-pointer ${
                  exportScope === 'filtered' ? 'bg-[#004aad] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Filtrados ({filteredClients.length})
              </button>
              <button
                onClick={() => setExportScope('all')}
                className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-all cursor-pointer ${
                  exportScope === 'all' ? 'bg-[#004aad] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Todos ({clients.length})
              </button>
            </div>
          </div>

          {!hasData && (
            <div className="px-4 py-3 text-xs text-gray-400 text-center">
              Nenhum creator para exportar
            </div>
          )}

          {hasData && (
            <div className="p-2">
              <button
                onClick={() => handleExport('csv')}
                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
              >
                <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center shrink-0">
                  <i className="ri-file-text-line text-emerald-600"></i>
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900">CSV</p>
                  <p className="text-xs text-gray-400">Planilha simples, compatível com qualquer editor</p>
                </div>
              </button>

              <button
                onClick={() => handleExport('xlsx')}
                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
              >
                <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center shrink-0">
                  <i className="ri-file-excel-2-line text-emerald-600"></i>
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900">Excel (.xlsx)</p>
                  <p className="text-xs text-gray-400">Planilha formatada com colunas ajustadas</p>
                </div>
              </button>

              <button
                onClick={() => handleExport('pdf')}
                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
              >
                <div className="w-8 h-8 bg-rose-50 rounded-lg flex items-center justify-center shrink-0">
                  <i className="ri-file-pdf-2-line text-rose-600"></i>
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900">PDF</p>
                  <p className="text-xs text-gray-400">Relatório formatado para impressão</p>
                </div>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
